import React, { useMemo, useRef, useState } from 'react';
import { handleElementKeyEvent } from '../../../util/events.util';
import Icon, { IconType } from '../../icons/Icon';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { t } from '../../../types/translation/Translator';

interface FileInputProps {
  onFiles: (files: FileList | null) => void;
  acceptedTypes?: string;
  multiple?: boolean;
}

export default function FileInput({ onFiles: onFile, acceptedTypes, multiple = false }: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [dragging, setDragging] = useState(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!event.dataTransfer.files.length) return;
    onFile(event.dataTransfer.files);
  };

  const openFilePicker = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files?.length) onFile(event.currentTarget.files);
    event.currentTarget.value = '';
  };

  const borderType = useMemo(() => {
    if (dragging) return 'border-solid';
    return 'border-dashed';
  }, [dragging]);

  const backgroundColor = useMemo(() => {
    if (dragging) return 'bg-blue-50';
    return 'bg-gray-50';
  }, [dragging]);

  return (
    <div
      tabIndex={0}
      onDrop={handleDrop}
      onDragOver={e => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragEnter={e => setDragging(true)}
      onDragLeave={e => setDragging(false)}
      onClick={openFilePicker}
      onKeyDown={event => handleElementKeyEvent(event, 'Enter', openFilePicker)}
      className={`flex items-center justify-center border ${borderType} rounded-2 border-blue-900 py-10 ${backgroundColor} cursor-pointer`}
    >
      <>
        <input
          multiple={multiple}
          ref={inputRef}
          type='file'
          className='hidden'
          onChange={handleInputChange}
          accept={acceptedTypes}
        />

        <div className='flex flex-col items-center justify-center gap-1 text-center'>
          {dragging ? (
            <>
              <Icon type={IconType.uploadCloud2} stroke={'stroke-black'} size={'h-12 w-12'} strokeWidth={'stroke-1'} />
              <div>
                <p className='font-medium text-black'>{t(TranslationKey.fileUploadDropFilesToUpload).singular.label}</p>
                <p className='invisible text-sm'>.</p>
              </div>
            </>
          ) : (
            <>
              <Icon type={IconType.filePlus2} stroke={'stroke-blue-900'} size={'h-12 w-12'} strokeWidth={'stroke-1'} />
              <div className='max-w-md break-words'>
                <p className='font-medium text-black'>
                  {t(TranslationKey.fileUploadSelectFiles).singular.label} <br />(
                  {t(TranslationKey.fileUploadSupportedFileFormats).singular.lower}:{' '}
                  {acceptedTypes?.split(',').map((type, idx, arr) => (
                    <React.Fragment key={type}>
                      {type.trim()}
                      {idx < arr.length - 1 && (
                        <>
                          , <wbr />
                        </> // Line Break Opportunity
                      )}
                    </React.Fragment>
                  ))}
                  ){' '}
                </p>
              </div>
            </>
          )}
        </div>
      </>
    </div>
  );
}
